import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { ArrowRight, CheckCircle2, Plus } from 'lucide-react'
import { AccountLayout } from '@/components/account/AccountLayout'
import { AccountPageHeader } from '@/components/account/AccountPageHeader'
import { UsageOverview } from '@/components/account/UsageOverview'
import { useAuth } from '@/lib/auth'
import { useActiveOrg, useUserProfile } from '@/lib/user-profile'
import { listMyApiKeys, type ApiKeyRow } from '@/lib/api/api-keys'

function statusOf(k: ApiKeyRow): 'active' | 'expired' | 'revoked' {
  if (k.revoked_at) return 'revoked'
  if (k.expires_at && new Date(k.expires_at) < new Date()) return 'expired'
  return 'active'
}

function shortDate(s: string | null): string {
  if (!s) return '—'
  return new Date(s).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function AccountOverviewPage() {
  const { user, loading: authLoading } = useAuth()
  const { org, refresh: refreshOrg } = useActiveOrg()
  const { refresh: refreshProfile } = useUserProfile()
  const orgId = org?.organization_id ?? null
  const isOrgAdmin = org?.caller_role === 'owner' || org?.caller_role === 'admin'

  const [keys, setKeys] = useState<ApiKeyRow[]>([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    if (!orgId) {
      setKeys([])
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const rows = await listMyApiKeys(orgId)
      setKeys(rows)
    } catch {
      setKeys([])
    } finally {
      setLoading(false)
    }
  }, [orgId])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load()
  }, [load])

  const activeKeyCount = useMemo(
    () => keys.filter((k) => statusOf(k) === 'active').length,
    [keys],
  )

  if (!authLoading && !user) return <Navigate to="/sign-in" replace />

  return (
    <AccountLayout>
      <AccountPageHeader
        title="Account"
        subtitle={<>At-a-glance view of your organization&rsquo;s keys + usage.</>}
        orgId={orgId ?? ''}
        onOrgChange={async () => {
          await Promise.all([refreshProfile(), refreshOrg(), load()])
        }}
      />
      <OverviewRedirectBanner />

      <UsageOverview orgId={orgId} activeKeyCount={activeKeyCount} />

      {isOrgAdmin ? (
        <div className="mt-3 text-right">
          <Link
            to="/account/reporting"
            className="text-[13px] text-[var(--color-accent)] hover:underline underline-offset-2 inline-flex items-center gap-1"
          >
            View full report
            <ArrowRight size={13} aria-hidden />
          </Link>
        </div>
      ) : null}

      <section className="mt-8 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-elev)] p-6">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-[14px] font-semibold uppercase tracking-[0.08em] text-[var(--color-muted)] font-[var(--font-mono)]">
            Keys
          </h2>
          <Link
            to="/account/keys"
            className={[
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[var(--radius-DEFAULT)]',
              'bg-[var(--color-accent)] text-[var(--color-surface)]',
              'text-[12.5px] font-medium',
              'hover:bg-[var(--color-accent-hover)] transition-colors cursor-pointer',
            ].join(' ')}
          >
            <Plus size={13} aria-hidden />
            Manage keys
          </Link>
        </div>

        {loading ? (
          <p className="mt-4 text-[13px] text-[var(--color-muted)]">Loading…</p>
        ) : keys.length === 0 ? (
          <div className="mt-4 text-[13px] text-[var(--color-muted)]">
            No keys yet.{' '}
            <Link to="/account/keys" className="text-[var(--color-accent)] hover:underline underline-offset-2 inline-flex items-center gap-1">
              Generate your first
              <ArrowRight size={12} aria-hidden />
            </Link>
          </div>
        ) : (
          <ul className="mt-4 divide-y divide-[var(--color-border)]">
            {keys.slice(0, 8).map((k) => {
              const s = statusOf(k)
              return (
                <li key={k.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <p className="text-[13.5px] font-medium text-[var(--color-text-strong)] truncate">
                      {k.name}{' '}
                      <span className="font-[var(--font-mono)] text-[12px] text-[var(--color-muted)]">
                        {k.key_prefix}…
                      </span>
                    </p>
                    <p className="text-[12px] text-[var(--color-muted)] truncate">
                      Created {shortDate(k.created_at)} · Last used {shortDate(k.last_used_at)} ·{' '}
                      Expires {k.expires_at ? shortDate(k.expires_at) : 'never'}
                    </p>
                  </div>
                  <StatusBadge status={s} />
                </li>
              )
            })}
          </ul>
        )}
        {keys.length > 8 ? (
          <p className="mt-3 text-[12px] text-[var(--color-muted)]">
            <Link to="/account/keys" className="hover:underline underline-offset-2">
              See all {keys.length} keys →
            </Link>
          </p>
        ) : null}
      </section>
    </AccountLayout>
  )
}

/**
 * Post-redirect banner for the Account landing. Supabase Auth's
 * signup-confirm flow redirects to /account?confirmed=1 — without
 * handling here the URL param sits stale and the user gets no
 * acknowledgment of the confirmation. PR #11 review flagged
 * this after the AccountPage split moved the banner to
 * /account/profile.
 */
function OverviewRedirectBanner() {
  const location = useLocation()
  const navigate = useNavigate()
  const params = new URLSearchParams(location.search)
  const message =
    params.get('confirmed') === '1' ? 'Your email is confirmed. Welcome to GeoBorder.' : null

  useEffect(() => {
    if (!message) return
    const timer = window.setTimeout(() => {
      params.delete('confirmed')
      const next = params.toString()
      navigate({ pathname: location.pathname, search: next ? `?${next}` : '' }, { replace: true })
    }, 5000)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message])

  if (!message) return null
  return (
    <div
      role="status"
      className={[
        'mb-6 flex items-center gap-3',
        'rounded-[var(--radius-DEFAULT)] border border-[var(--color-accent)]/40',
        'bg-[var(--color-accent-soft)] px-4 py-3',
        'text-[13.5px] text-[var(--color-text-strong)]',
      ].join(' ')}
    >
      <CheckCircle2 size={16} className="text-[var(--color-accent)] shrink-0" aria-hidden />
      <span>{message}</span>
    </div>
  )
}

function StatusBadge({ status }: { status: 'active' | 'expired' | 'revoked' }) {
  const cls = status === 'active'
    ? 'bg-[var(--color-accent-soft)] text-[var(--color-accent)]'
    : status === 'expired'
      ? 'bg-[var(--color-signal-soft)] text-[var(--color-signal)]'
      : 'bg-[var(--color-border)] text-[var(--color-muted)]'
  return (
    <span className={`px-2 py-0.5 rounded-[var(--radius-DEFAULT)] text-[11px] font-medium capitalize ${cls}`}>
      {status}
    </span>
  )
}
